import type { RuTranslation } from "./ru";

const en = {
  meta: {
    title: "Lexibit — Vocabulary practice",
    description:
      "Build word sets, practise every day, and break down words with an AI tutor. Works offline.",
  },
  common: {
    appName: "Lexibit",
    appSubtitle: "Vocabulary practice",
    cancel: "Cancel",
    confirm: "Continue",
    delete: "Delete",
    save: "Save",
    add: "Add",
    open: "Open",
    back: "Back",
    loading: "Loading…",
    error: "Error",
    ok: "OK",
    close: "Close",
    skip: "Skip",
    next: "Next",
    morePages: "More pages",
    start: "Start",
    all: "All",
    offlineFirstFooter: "Works offline",
    cards_one: "{{count}} card",
    cards_few: "{{count}} cards",
    cards_many: "{{count}} cards",
    cards_other: "{{count}} cards",
  },
  nav: {
    home: "Home",
    decks: "My words",
    drill: "Practice",
    aiTutor: "AI tutor",
    students: "Students",
    settings: "Settings",
    profile: "Profile",
    menu: "Menu",
    skipToContent: "Skip to content",
  },
  theme: {
    switchToLight: "Switch to light theme",
    switchToDark: "Switch to dark theme",
    system: "System",
    light: "Light",
    dark: "Dark",
  },
  onboarding: {
    pairTitle: "Language pair",
    pairDescription:
      "Choose the language you speak and the language you are learning.",
    primaryLabel: "I know",
    learningLabel: "I'm learning",
    directionHint:
      "A card shows the language you are learning, then its meaning in your primary language.",
    preparing: "Preparing your first practice…",
    choicePackTitle: "Ready-made set",
    choicePackDesc: "A1-level words and a 3-card practice",
    choiceCustomTitle: "My words",
    choiceCustomDesc: "An empty set, you add the words yourself",
    preparingCustom: "Preparing your set…",
    customError: "Couldn't create the set. Check your connection and try again.",
    successTitle: "First practice complete",
    successDescription:
      "You completed three cards. Progress is saved on this device.",
    optionalNameLabel: "Name (optional)",
    optionalNameHint: "You can add your name later in your profile.",
    finish: "Go home",
    step5_loadError:
      "Couldn't load the sets. Check your connection and try again.",
    step5_retry: "Try again",
    step5_installError:
      "Couldn't install the set. Check your connection and try again.",
  },
  decks: {
    title: "My words",
    subtitle: "Your own words and the sets you built",
    createAriaLabel: "Create set",
    cancelAriaLabel: "Cancel",
    namePlaceholder: "Name, for example “Spanish verbs”",
    fromLang: "From language",
    toLang: "To language",
    topic: "Topic",
    ageGroup: "Age group",
    level: "Level",
    advancedToggle: "Advanced",
    references: "Tags & categories",
    addButton: "Add",
    empty: "No sets of your own yet.",
    emptyExplain:
      "A set of your own is built from your words and joins practice alongside ready-made sets.",
    installedPacksTitle: "Installed sets",
    openLibrary: "Open Sets",
    createManual: "Create manually",
    deleteConfirmTitle: "Delete “{{name}}”?",
    deleteConfirmDesc:
      "All cards in the set will be deleted. This cannot be undone.",
    openDeckAriaLabel: "Open set {{name}}",
    deleteDeckAriaLabel: "Delete set {{name}}",
    dueCount: "{{count}} due",
  },
  deckView: {
    notFound: "Set not found.",
    backToDecks: "Back to my words",
    searchPlaceholder: "Search cards…",
    searchAriaLabel: "Search cards",
    menuAriaLabel: "Set actions",
    menuTrain: "Practice ({{count}})",
    menuCram: "All cards ({{count}})",
    menuCramTitle: "Show every card without waiting for its review",
    menuReverse: "Add reverse cards",
    menuReverseDisabledTitle: "This set has no languages",
    menuReverseTitleEnabled:
      "Each card gets a pair with the translation reversed",
    menuDeleteDeck: "Delete set",
    dueCount: "{{count}} due",
    emptyDeck: "No cards yet. Add the first one below.",
    emptySearch: "No results for “{{query}}”.",
    reverseAlready: "Every card already has a reverse pair.",
    reverseCreated: "Reverse cards added: {{count}}.",
    addCard: "Add card",
    addFromPack: "Add from a ready-made set",
    frontPlaceholder: "Word",
    backPlaceholder: "Translation",
    frontAriaLabel: "Word",
    backAriaLabel: "Translation",
    addCardButton: "Add card",
    addedStatus: "Added: {{preview}}",
    backToDecksAriaLabel: "Back to my words",
    cardFresh: "New · due now",
    cardRepeat_one: "Review: {{date}} · {{count}} time",
    cardRepeat_other: "Review: {{date}} · {{count}} times",
    deleteCardAriaLabel: "Delete card {{front}}",
    deleteCardTitle: "Delete the card “{{label}}”?",
    deleteCardDesc: "Progress for this card will be deleted.",
    deleteDeckConfirmTitle: "Delete “{{name}}”?",
    deleteDeckConfirmDesc:
      "All cards in the set will be deleted. This cannot be undone.",
  },
  drill: {
    loading: "Loading…",
    exitAriaLabel: "Exit",
    exitTitle: "Exit (Esc)",
    noLanguages_title: "Two languages",
    noLanguages_body:
      "Practice runs between two languages. Choose them in Settings.",
    noLanguages_cta: "Go to Settings",
    noPacks_title: "No words yet",
    noPacks_body: "Install a ready-made set or add your own words.",
    noPacks_cta: "Go to Sets",
    emptyCards_title: "No matching cards",
    emptyCards_body: "Your sets have no words in both of the chosen languages.",
    sessionDone_title: "Practice finished",
    sessionDone_body:
      '$t(common.cards, {"count": {{reviewed}} }) · correct: {{correct}} · incorrect: {{incorrect}} · accuracy {{accuracy}}%',
    addWordCta: "Add your own word",
    again_cta: "Again",
    toHome: "Home",
    flipHint: "Click to flip",
    answerLabel: "Answer",
    cardNew: "new",
    cardLearning: "learning",
    cardFamiliar: "familiar",
    alsoLabel: "also: {{aliases}}",
    gradeAgain: "Again",
    gradeHard: "Hard",
    gradeGood: "Good",
    gradeEasy: "Easy",
  },
  myWords: {
    sectionTitle: "My words",
    wordsCount_one: "{{count}} word",
    wordsCount_few: "{{count}} words",
    wordsCount_many: "{{count}} words",
    wordsCount_other: "{{count}} words",
    summaryZero: "No words of your own yet. Add the first one",
    addButton: "Add word",
    addTitle: "Add a word",
    addDesc: "The word goes into My Words and into today's practice.",
    wordLabel: "Word",
    translationLabel: "Translation",
    swapDirection: "Swap direction",
    submitButton: "Add",
    doneButton: "Done",
    addedCount_one: "{{count}} word added",
    addedCount_few: "{{count}} words added",
    addedCount_many: "{{count}} words added",
    addedCount_other: "{{count}} words added",
    defaultDeckName: "My words",
  },
  dashboard: {
    welcomeTitle: "Welcome to Lexibit",
    welcomeDesc: "Pick a ready-made set or add your own words",
    openLibrary: "Open Sets",
    createManual: "Create manually",
    greetingMorning: "Good morning",
    greetingDay: "Good afternoon",
    greetingEvening: "Good evening",
    streakMotivate_one: "You practised today. Streak: {{count}} day",
    streakMotivate_other: "You practised today. Streak: {{count}} days",
    streakStart: "A short practice keeps your streak",
    statNew: "New",
    statNewHint: "not yet studied",
    statStreak: "Streak",
    statStreakHintZero: "start today",
    statStreakHint: "days in a row",
    activityTitle: "Activity",
    activitySubtitle: "Last 14 days",
    activityLess: "less",
    activityMore: "more",
    goalToday: "Today",
    goalReached: "daily goal reached",
    goalDay: "daily goal",
    goalAccuracy: "accuracy {{accuracy}}%",
    deckHeatmapTitle_one: "{{date}}: {{count}} card",
    deckHeatmapTitle_other: "{{date}}: {{count}} cards",
    assignments_title: "Assignments from tutor",
    assignments_loadError: "Failed to load assignments",
    assignments_open: "Open",
    assignments_import: "Import",
    assignments_importing: "Importing…",
    assignments_importError: "Import error",
    heroTitle: "Ready when you are",
    heroDescIdle_one:
      '$t(myWords.wordsCount, {"count": {{count}} }) ready for practice',
    heroDescIdle_other:
      '$t(myWords.wordsCount, {"count": {{count}} }) ready for practice',
    heroDescProgress: "Today {{reviewed}} of {{goal}}. Keep going",
    heroDescPlan_one:
      "{{count}} card in this practice: {{due}} due and {{newCount}} new.",
    heroDescPlan_other:
      "{{count}} cards in this practice: {{due}} due and {{newCount}} new.",
    startDrill: "Start practice",
    reviewDrill_one: "Practice {{count}} card",
    reviewDrill_other: "Practice {{count}} cards",
    statDue: "Due",
    statDueHint: "today",
    packsSection: "Sets",
    aiTutorTeaserTitle: "AI tutor",
    aiTutorTeaserDesc: "Break down words in a live conversation",
  },
  library: {
    title: "Sets",
    titleForDeck: "Add to “{{name}}”",
    subtitleForDeck: "Words from the set you pick land here and join practice.",
    subtitle:
      "Ready-made word sets by language, topic, age and level. Installing creates your own copy; the original set stays unchanged.",
    stats:
      '$t(library.decksCount, {"count": {{decks}} }) · $t(library.cardsCount, {"count": {{cards}} })',
    installedBanner: "Installed: ",
    openInstalled: "Open",
    hideNotification: "Dismiss",
    filterTitle: "Filter",
    filterClear: "Clear",
    filterFrom: "From language",
    filterTo: "To language",
    filterTopic: "Topic",
    filterAge: "Age group",
    filterLevel: "Level",
    filterAny: "Any",
    filteredStats:
      'Showing $t(library.decksCount, {"count": {{decks}} }) · $t(library.cardsCount, {"count": {{cards}} })',
    emptyFilter: "Nothing matches the filter.",
    resetFilters: "Clear filters",
    install: "Install",
    installing: "Installing…",
    cardsCount_one: "{{count, number}} card",
    cardsCount_other: "{{count, number}} cards",
    decksCount_one: "{{count, number}} set",
    decksCount_other: "{{count, number}} sets",
  },
  packList: {
    title: "Ready-made sets",
    subtitle: "Words go straight into your practice",
    installed: "installed",
    progressLabel: "{{mastered}} of {{total}} mastered",
    recommended: "Start with this",
    levelOther: "Other",
    conceptCount_one: "{{count}} word",
    conceptCount_other: "{{count}} words",
    languageCount_one: "{{count}} language",
    languageCount_other: "{{count}} languages",
    install: "Install",
    installNamed: "Install set {{name}}",
    installSuccess: "Installed {{name}}",
    deleteAriaLabel: "Remove set {{name}}",
    deleteTitle: "Remove set",
    confirmTitle: "Remove set?",
    confirmDesc: "The set and all progress for its words will be deleted.",
    loading: "Loading sets…",
    loadError: "Couldn't load the sets. Check your connection and try again.",
    retry: "Try again",
    installError: "Couldn't install the set. Try again.",
    removeError: "Couldn't remove the set. Try again.",
    openTraining: "Practice",
    searchLabel: "Search sets",
    searchPlaceholder: "Name or description",
    levelLabel: "CEFR level",
    emptyFilter: "No sets match this search.",
    showMore: "Show {{count}} more",
  },
  settings: {
    title: "Settings",
    sectionAccount: "Account",
    profileLabel: "Profile",
    profileEmpty: "Set up your profile",
    cloudLabel: "Cloud sync",
    cloudLinkedError: "Account linked. The last sync failed",
    cloudLinkedSynced: "Synced · {{time}}",
    cloudLinkedAuto: "Syncs automatically",
    cloudOffline: "Not connected. Data stays on this device",
    cloudNotConfigured:
      "Not available in this build. Data stays on this device",
    cloudStatusLinked: "On",
    cloudStatusOffline: "This device only",
    cloudStatusUnavailable: "Unavailable",
    sectionTutor: "Tutor",
    inviteLabel: "Invitation code",
    inviteDesc: "Enter the code or link from your tutor",
    invitePlaceholder: "Code or link",
    inviteApply: "Apply",
    inviteApplying: "…",
    inviteSuccess: "You are connected to your tutor",
    inviteNeedAuth: "Sign in first",
    inviteNetError: "Network error. Check your connection.",
    sectionLanguages: "Languages",
    primaryLangLabel: "Primary language",
    primaryLangDesc: "Your native language. Cards are translated into it",
    learningLangsLabel: "Languages to study",
    learningLangsDesc:
      "The language you practise now. The others stay available",
    manageLearningLangs: "Manage study languages ({{count}})",
    primaryIsThisLang: "This is your primary language",
    sectionDirections: "Card side",
    directionsDesc_one:
      "Which language shows first. Active directions: {{count}}",
    directionsDesc_other:
      "Which language shows first. Active directions: {{count}}",
    sectionTraining: "Practice",
    dailyGoalLabel: "Daily goal",
    dailyGoalDesc: "How many cards a day",
    dailyGoalAriaLabel: "Daily goal",
    defaultModeLabel: "Default mode",
    defaultModeDesc: "What a practice session starts with",
    sectionData: "Data",
    localStorageLabel: "This device",
    localStorageDesc:
      "Back up or clear this device only. The cloud is untouched",
    exportButton: "Export data",
    importButton: "Import data",
    deleteAllButton: "Delete all data",
    localDeleteButton: "Clear this device",
    localDeleteConfirmTitle: "Clear data on this device?",
    localDeleteConfirmDesc:
      "Sets and progress on this device will be removed and the account disconnected. Cloud data stays.",
    cloudDangerLabel: "Cloud data and account",
    cloudDangerDesc: "Applies to every device with this account",
    cloudDeleteButton: "Delete cloud data",
    cloudDeleteConfirmTitle: "Delete cloud data?",
    cloudDeleteConfirmDesc:
      "Sets, progress and the tutor link will be deleted from the cloud and this device. Your account stays.",
    cloudDeleteError:
      "Couldn’t delete cloud data. Nothing on this device was removed.",
    localCleanupAfterCloudError:
      "Cloud data was deleted, but this device could not be cleared. Restart the app and clear it again.",
    accountDeleteButton: "Delete account",
    accountDeleteGuestDesc:
      "Sign in to delete your account and cloud data. Right now this device holds only a local copy; delete it above.",
    accountDeleteConfirmTitle: "Delete account?",
    accountDeleteConfirmDesc:
      "Your account, cloud data and data on this device will be deleted. This cannot be undone.",
    accountDeleteError: "Couldn’t delete the account. Try again.",
    localCleanupAfterAccountError:
      "The account was deleted, but this device could not be cleared. Restart the app and clear it again.",
    analyticsLabel: "Anonymous usage stats",
    analyticsDesc:
      "Helps improve the app. Your name and card text are never sent",
    wordOfDaySaveError:
      "Could not save this setting. Check your connection and try again.",
    wordOfDayLabel: "Word of the day",
    wordOfDayDesc: "One due word a day, sent by the bot",
    pushLabel: "Browser notifications",
    pushDesc: "Without them, review reminders only appear when you open the app.",
    pushEnable: "Enable",
    pushDismiss: "Not now",
    pushSaveError: "Could not turn this on. Check your browser's notification permission and try again.",
    termsPrivacyLabel: "Terms and privacy",
    importConfirmTitle: "Import data?",
    importConfirmDesc:
      "Import replaces all sets, progress and settings on this device.",
    importConfirmLabel: "Import",
    deleteConfirmTitle: "Delete all data?",
    deleteConfirmDesc:
      "All sets, cards and progress will be deleted. This action cannot be undone.",
    exportSaved: "Saved: {{filename}}",
    exportError: "Failed to export",
    importSuccess_one: "Imported {{count}} record. Reloading…",
    importSuccess_other: "Imported {{count}} records. Reloading…",
    importError: "Failed to import",
    studyModeFlashcard: "Show the answer when tapped",
    studyModeReverse: "Show the translation first",
    studyModeTyping: "Typing",
    studyModeChoice: "Multiple choice",
    studyModeMatch: "Matching",
    studyModeDrill: "Practice (shared queue)",
    themeLabel: "Appearance",
    themeDesc: "Follow your system or choose a theme",
    uiLanguageLabel: "Interface language",
    uiLanguageAuto: "Auto",
    uiLanguageRu: "Русский",
    uiLanguageEn: "English",
    uiLanguageKo: "한국어",
    sectionUiLanguage: "Interface language",
    uiLanguageDesc: "Language of buttons, menus and messages",
    addToHomeScreenLabel: "Add to home screen",
    addToHomeScreenDesc: "Open Lexibit straight from your phone's home screen.",
  },
  profile: {
    backToSettings: "Back to settings",
    editButton: "Edit",
    nameLabel: "Name",
    nameRequired: "Enter a name, or cancel to keep the current one.",
    saveButton: "Save",
    cancelButton: "Cancel",
    noName: "No name",
    joinedAt: "In Lexibit since {{date}}",
    statWords: "Words",
    statDue: "Due",
    statMastered: "Mastered",
    statStreak: "Streak",
    roleLabel: "Role",
    roleStudent: "Student",
    roleTutor: "Tutor",
  },
  aiTutor: {
    title: "AI tutor",
    subtitle: "Break down words in a conversation",
    subtitleOnline: "Ask about a word, a rule or a phrase",
    connectAccount: "Connect your account",
    connectDesc:
      "Go to <link>Settings → Account</link> to chat with the AI tutor.",
    newDialog: "New conversation",
    emptyHint: "Ask about a word, a rule or a phrase.",
    inputPlaceholder: "Type a message…",
    sendAriaLabel: "Send",
    inputHint: "Enter to send, Shift+Enter for new line",
    errorNetwork: "Network error. Check your connection.",
  },
  tutor: {
    title: "Tutor dashboard",
    subtitle: "Your sets and assignments",
    createDeckAriaLabel: "Create set",
    connectAccount: "Connect your account",
    connectDesc:
      "Go to <link>Settings → Account</link> to invite students and assign sets.",
    inviteSection: "Invite a student",
    inviteLinkValid: "Link is valid until {{date}}. Send it to your student.",
    inviteCopyTitle: "Copy",
    inviteCopyError:
      "Could not copy the link. Use Share or select the link manually.",
    inviteCreateAnother: "Create another",
    inviteDesc: "Create a one-time link. It works for 7 days.",
    inviteDeckLabel: "First assignment (optional)",
    inviteDeckNone: "Connect only, no assignment",
    inviteDeckHint: "The student opens this assignment right after connecting.",
    inviteCreate: "Create invitation",
    inviteCreating: "Creating…",
    inviteNotTutor:
      "Your tutor profile is still being created. Wait a moment and try again.",
    inviteError: "Couldn't create the invitation. Try again later.",
    inviteNeedAuth: "Sign in first",
    inviteNetError: "Network error. Check your connection.",
    studentsSection: "Students",
    studentsLoading: "Loading…",
    studentsEmpty: "No students yet. Send an invitation.",
    studentConnected: "Connected {{date}}",
    assignSection: "Assign a set",
    assignStudentLabel: "Student",
    assignStudentPlaceholder: "Select a student",
    assignDeckLabel: "Set",
    assignDeckPlaceholder: "Select a set",
    assignNotePlaceholder: "Note (optional)",
    assignButton: "Assign",
    assigning: "Assigning…",
    assignError: "Couldn't assign the set. Try again.",
    deleteAssignError: "Couldn't delete the assignment. Try again.",
    deleteAssignTitle: "Delete assignment",
    activeAssignments: "Active assignments",
    deckStatsDecks: "Sets",
    deckStatsCards: "Cards",
    yourDecks: "Your sets",
    allDecks: "All",
    noDecks: "No sets yet.",
    createDeck: "Create set",
    shareTitle: "Share",
    shareInvite: "Invitation to a tutor in Lexibit",
    shareInviteWithDeck:
      "Tutor invitation with the “{{name}}” assignment in Lexibit",
  },
  references: {
    title: "Tags & categories",
    subtitle:
      "Languages, topics, ages, CEFR levels and word types that sets are tagged with.",
    catalogTitle: "Set catalogue",
    catalogSubtitle:
      "Choose a language pair and a set, and practice starts right away.",
    catalogLoading: "Loading the catalogue…",
    catalogLoadError:
      "Couldn't load the catalogue. Check your connection and try again.",
    catalogRetry: "Try again",
    catalogStatsLabel: "Catalogue totals",
    statsDictionaries: "sets",
    statsLocales: "languages",
    statsDirections: "language directions",
    statsCardsInPacks: "cards",
    pairTitle: "Choose a language pair",
    pairSubtitle:
      "Direction matters: you see the prompt language and type the answer.",
    fromLabel: "Prompt language",
    toLabel: "Answer language",
    swapPair: "Swap the languages",
    pairLoadingCounts: "Matching sets: {{dictionaries}} · counting cards…",
    pairSummary:
      "Matching sets: {{dictionaries}} · cards available: {{cards, number}}",
    pairSummaryPartial:
      "Sets loaded: {{dictionaries}} · cards available: {{cards, number}} · didn't open: {{failed}}",
    retryUnavailable: "Load the rest",
    catalogEmptyTitle: "No sets for this pair yet",
    catalogEmptyBody:
      "Choose another language pair. The tag reference below stays available.",
    selectDictionary: "Choose a set",
    selected: "Selected",
    dictionaryCardsLoading: "Counting cards…",
    dictionaryCards_one: "{{count, number}} card",
    dictionaryCards_other: "{{count, number}} cards",
    dictionaryLanguages_one: "{{count}} language",
    dictionaryLanguages_other: "{{count}} languages",
    startTyping_one: "Start typing · {{count, number}} card",
    startTyping_other: "Start typing · {{count, number}} cards",
    startingTyping: "Preparing practice…",
    catalogNoCards: "This set has no cards for the chosen language pair.",
    dictionaryLoadError: "Couldn't load the set. Reload the page.",
    catalogStartError: "Couldn't start the practice. Try again.",
    catalogUpdatedCopy:
      "The set in the catalogue changed. A new copy was created; your earlier progress stays in the old one.",
    taxonomyTitle: "Tag reference",
    taxonomySummary: "Languages, topics, ages, CEFR levels and word types.",
    difficulty: "difficulty",
    tabLanguages: "Languages",
    tabTopics: "Topics",
    tabAges: "Ages",
    tabLevels: "Levels",
    tabEntities: "Word types",
    footerDocs: "Architecture details: ",
  },
  cloudLink: {
    title: "Cloud sync",
    unconfiguredHint:
      "Sync isn't available in this version. Data stays on this device.",
    checkingSession: "Checking your sign-in…",
    telegramHint: "Sign in with Telegram, no password needed.",
    localOnly: "Data is stored on this device only",
    localOnlyHint: "Connect the cloud to carry on from other devices.",
    connectMiniApp: "Connect cloud sync",
    signingIn: "Signing in…",
    signInError: "Couldn't sign in. Try again.",
    connected: "Cloud connected",
    connectedWithName: "Cloud connected · {{name}}",
    connectedWithTelegram: "Linked to Telegram @{{username}}",
    syncLabel: "Synced {{time}}",
    syncAuto: "Syncing automatically",
    syncError: "The last sync failed",
    signOut: "Sign out",
  },
  hostAuth: {
    title: "Sign in",
    choiceBody:
      "If you already use Lexibit in another messenger, link the accounts, or you will end up with a second one.",
    newAccount: "Continue as a new account",
    existingAccount: "I already have an account",
    linkBody:
      "On a device you are signed in on, open Profile → Linked messengers and create a one-time code.",
    codeLabel: "One-time link code",
    linkAndContinue: "Link and continue",
    signingIn: "Verifying with the messenger…",
    continueLocal: "Continue on this device without cloud sync",
    errors: {
      invalid_code: "The code format is invalid.",
      code_used: "This code has already been used. Generate a new one.",
      code_expired: "This code has expired. Generate a new one.",
      too_many_attempts: "Too many attempts. Generate a new code.",
      identity_already_linked:
        "This messenger account is already linked to another Lexibit account.",
      provider_already_linked:
        "Another account from this messenger is already linked.",
      invalid_provider_proof:
        "The messenger couldn't verify your identity. Reopen Lexibit and try again.",
      provider_not_configured:
        "Sign-in for this messenger is not configured yet.",
      network: "Couldn't reach the sign-in service. Check your connection.",
      internal: "Sign-in is temporarily unavailable. Try again.",
      generic: "Couldn't sign in. Try again.",
    },
  },
  accountLink: {
    title: "Linked messengers",
    body: "Generate a one-time code, then enter it before the first sign-in in Telegram, LINE, Discord or Teams. The code cannot sign in by itself.",
    generate: "Create a link code",
    regenerate: "Create a new code",
    copy: "Copy code",
    expires: "Valid until {{time}} · one use only",
    createError: "Couldn't create the code. Try again.",
    copyError: "Couldn't copy it automatically. Select the code manually.",
  },
  backup: {
    reminderTitle: "Backup",
    reminderDesc:
      "It has been more than 14 days since your last export. Your data is only on this device.",
    reminderAction: "Settings",
  },
  telegramConsent: {
    tagline: "A few words every day so you don't forget what you learned.",
    valueLine: "After you sign in, your progress shows up on all your devices.",
    title: "Data processing consent",
    body: "To sign you in, Lexibit reads your Telegram ID, name and username from Telegram. Your sets and progress are stored under this account, so you can carry on from any device.",
    policyLink: "Privacy policy",
    accept: "Accept and sign in",
    signingIn: "Signing in…",
    decline: "Not now",
    error: "Couldn't sign in. Try again.",
  },
  joinInvite: {
    invalid: "This invitation link is invalid.",
    notFound: "This invitation is missing, already used, or expired.",
    cannotInviteSelf: "You cannot accept your own invitation.",
    unauthenticated: "Connect your account first.",
    genericError:
      "Couldn't accept the invitation. Check your connection and try again.",
    cloudUnavailable:
      "The cloud is not set up yet. The invitation is saved until the next launch.",
    title: "Tutor invitation",
    body: "After you confirm, your tutor can assign sets and see your results.",
    successTitle: "Tutor connected",
    successAssignment:
      "Your first assignment is ready and opens on the home screen.",
    successNoAssignment: "New assignments will appear on your home screen.",
    openAssignment: "Open assignment",
    goHome: "Go home",
    needAuth: "Connect your account, then tap “Accept invitation” again.",
    connecting: "Connecting…",
    accept: "Accept invitation",
    notNow: "Not now",
  },
  notFound: {
    title: "Page not found",
    routeError: "This screen could not be opened.",
    body: "The link may be outdated or invalid.",
    back: "Back",
    home: "Home",
  },
  errorBoundary: {
    title: "This screen didn't open",
    body: "Your data on this device is safe.",
    retry: "Try again",
    home: "Go home",
  },
  a11y: {
    closeDialog: "Close dialog",
    closePanel: "Close panel",
    sidebarTitle: "Sidebar",
    sidebarDescription: "Displays the mobile navigation sidebar.",
    toggleSidebar: "Toggle sidebar",
    breadcrumb: "Breadcrumb",
    pagination: "Pagination",
    previousPage: "Go to previous page",
    nextPage: "Go to next page",
    previous: "Previous",
    next: "Next",
  },
  growthShare: {
    invalid: "This link doesn't work",
    missing: "This set is missing or the link has expired",
    home: "Go to Lexibit",
    challenge: "5-card challenge",
    sharedDeck: "Word set",
    tryNow: "Try it now, no account needed",
    reveal: "Tap to reveal",
    learning: "Still learning",
    knew: "I know it",
    done: "Done",
    score: "{{correct}} of {{total}} known",
    challengeFriend: "Challenge a friend",
    postValueChallenge: "Challenge a friend with 5 cards",
    postValueShareText:
      "I just practised {{title}}. Can you beat this 5-card Lexibit challenge?",
    sharing: "Preparing…",
    shareText: "Try this short Lexibit practice: {{title}}",
    share: "Share",
    manualCopyTitle: "Copy the link",
    manualCopyHint:
      "Automatic sharing is unavailable. Select and copy this link.",
    manualCopyLabel: "Share link",
    manualCopyClose: "Close",
    shareDeck: "Share {{title}}",
    shareToStory: "Share to story",
    shareToStoryDeck: "Share {{title}} to story",
    storyCaption: "Learning with Lexibit 📚",
    saveAndContinue: "Save this set and continue",
    saving: "Saving…",
    noAuthPreview: "A preview doesn't create an account.",
    applyError: "Couldn't save the set. Check your connection and try again.",
    results: {
      shared: "Shared",
      handoff: "Telegram is open. Send the message when you are ready.",
      copied: "Link copied",
      cancelled: "Sharing cancelled",
      failed: "Couldn't share",
    },
  },
  privacy: {
    title: "What Lexibit stores",
    body: "Signing in shares the verified account ID and available profile fields (such as name, username and avatar) from Telegram, LINE, Discord, Microsoft Teams, VK or MAX with Lexibit. We use them only to create, label and link your account — your sets and progress are stored under it so you can continue on another device. Provider tokens and raw link codes are not stored. Nothing is posted on your behalf, and your data is never sold.",
  },
  errors: {
    inviteNotFound: "Code not found or expired.",
    cannotInviteSelf: "You cannot use your own invitation code.",
    unauthenticated: "Sign in to use the invitation.",
    invalidBody: "Invalid code format.",
    inviteDefault: "Couldn't apply the invitation. Try again later.",
    aiAuthError: "Sign in to continue.",
    aiRateLimit: "Daily limit reached. Try again tomorrow.",
    aiBadRequest: "Couldn't send the message. Start a new conversation.",
    aiServerError: "Server error. Try again later.",
  },
  pwa: {
    updateTitle: "A new version is available",
    updateBody: "Reload the page to install it.",
    updateNow: "Reload",
    updateLater: "Later",
    statusLabel: "Offline access",
    status: {
      unsupported: "This browser does not support offline access.",
      installing: "Preparing this app for offline use.",
      ready: "Ready offline on this device.",
      update: "An update is ready to install.",
      error:
        "Couldn't set up offline access. Connect to the network and reload the page.",
    },
  },
} satisfies RuTranslation;

export default en;
